import React, { useState, useRef, useMemo, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import TagMultiSelect from "./TagMultiSelect";
import KpiCascadeFilter, { kpiMatchesCascadeSteps } from "../../../DataManagment/Quantative/component/KpiTagFilter";
import {
  generateTemplate,
  parseUploadedTemplate,
  validateParsedData,
  buildPayloads,
} from "../utils/templateUtils";
import { PERIOD_TYPE_LABELS } from "../hooks/useTriggers";

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeIn  = keyframes`from { opacity: 0 } to { opacity: 1 }`;
const slideUp = keyframes`from { opacity: 0; transform: translateY(20px) } to { opacity: 1; transform: translateY(0) }`;

// ─── Overlay & Modal shell ────────────────────────────────────────────────────
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  animation: ${fadeIn} 0.15s ease;
`;

const ModalBox = styled.div`
  background: #fff;
  border-radius: 18px;
  width: 100%;
  max-width: 820px;
  max-height: 92vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.2);
  animation: ${slideUp} 0.2s ease;
`;

// ─── Header ───────────────────────────────────────────────────────────────────
const ModalHeader = styled.div`
  background: linear-gradient(135deg, #3f88a5 0%, #2e6d88 100%);
  padding: 18px 24px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const HeaderIcon = styled.span`
  font-size: 20px;
  line-height: 1;
`;

const HeaderTitle = styled.h3`
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  margin: 0;
  letter-spacing: 0.2px;
`;

const HeaderSub = styled.p`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.72);
  margin: 0 0 0 auto;
`;

const CloseBtn = styled.button`
  background: rgba(255, 255, 255, 0.15);
  border: none;
  border-radius: 8px;
  color: #fff;
  font-size: 17px;
  line-height: 1;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-left: 8px;
  flex-shrink: 0;
  transition: background 0.15s;
  &:hover { background: rgba(255, 255, 255, 0.28); }
`;

// ─── Body ─────────────────────────────────────────────────────────────────────
const ModalBody = styled.div`
  overflow-y: auto;
  flex: 1;
  padding: 22px 24px;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
  &::-webkit-scrollbar { width: 5px; }
  &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
`;

const InfoBanner = styled.div`
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 12.5px;
  color: #0369a1;
  margin-bottom: 20px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  line-height: 1.5;
`;

// ─── Step layout ──────────────────────────────────────────────────────────────
const SectionLabel = styled.p`
  font-size: 11px;
  font-weight: 700;
  color: #3f88a5;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 8px;
`;

const FieldLabel = styled.p`
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin: 0 0 5px;
`;

const StyledSelect = styled.select`
  width: 220px;
  padding: 7px 10px;
  border: 1.5px solid #e2e8f0;
  border-radius: 9px;
  font-size: 13px;
  color: #1e293b;
  background: #fff;
  outline: none;
  transition: border-color 0.15s;
  &:focus { border-color: #3f88a5; }
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  & > * { min-width: 0; }
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1.5px solid #f1f5f9;
  margin: 20px 0;
`;

// ─── Download / Upload blocks ─────────────────────────────────────────────────
const Block = styled.div`
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
`;

const BlockHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const BlockTitle = styled.p`
  font-size: 12px;
  font-weight: 700;
  color: #1e293b;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin: 0;
`;

const BlockDesc = styled.p`
  font-size: 12px;
  color: #64748b;
  margin: 0 0 12px;
  line-height: 1.5;
`;

const DownloadBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #3f88a5;
  color: #fff;
  border: none;
  border-radius: 9px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, transform 0.1s;
  &:hover:not(:disabled) { background: #2e6d88; transform: translateY(-1px); }
  &:disabled { background: #cbd5e1; color: #94a3b8; cursor: not-allowed; transform: none; }
`;

const DisabledNote = styled.p`
  font-size: 11px;
  color: #94a3b8;
  margin: 6px 0 0;
  font-style: italic;
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 8px;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 8px;
  background: #e0f2fe;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  color: #0369a1;
`;

const UploadZone = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  border: 2px dashed ${({ $hasFile, $hasError }) =>
    $hasError ? "#ef4444" : $hasFile ? "#3f88a5" : "#cbd5e1"};
  border-radius: 10px;
  padding: 12px 14px;
  background: ${({ $hasFile, $hasError }) =>
    $hasError ? "#fef2f2" : $hasFile ? "#f0f9ff" : "#fff"};
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  &:hover { border-color: #3f88a5; background: #f0f9ff; }
`;

const UploadIcon = styled.span`
  font-size: 22px;
  flex-shrink: 0;
  line-height: 1;
`;

const UploadText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const UploadPrimary = styled.span`
  font-size: 12.5px;
  color: ${({ $hasFile }) => ($hasFile ? "#0369a1" : "#64748b")};
  font-weight: ${({ $hasFile }) => ($hasFile ? 600 : 500)};
  word-break: break-all;
`;

const UploadSecondary = styled.span`
  font-size: 11px;
  color: #94a3b8;
`;

const ParseSummary = styled.div`
  margin-top: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  background: ${({ $ok }) => ($ok ? "#f0fdf4" : "#fef2f2")};
  border: 1px solid ${({ $ok }) => ($ok ? "#bbf7d0" : "#fecaca")};
`;

const SummaryLine = styled.p`
  font-size: 12px;
  color: ${({ $ok }) => ($ok ? "#16a34a" : "#ef4444")};
  font-weight: 500;
  margin: 0 0 2px;
  &:last-child { margin: 0; }
`;

const ErrorList = styled.ul`
  margin: 5px 0 0;
  padding-left: 16px;
`;

const ErrorItem = styled.li`
  font-size: 11px;
  color: #ef4444;
  margin-bottom: 2px;
  line-height: 1.4;
`;

const ConfirmCard = styled.div`
  margin-top: 10px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #f0f9ff;
  border: 1.5px solid #bae6fd;
`;

const ConfirmTitle = styled.p`
  font-size: 11px;
  font-weight: 700;
  color: #0369a1;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin: 0 0 8px;
`;

const ConfirmGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: 6px;
`;

const ConfirmItem = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 7px 9px;
  border: 1px solid #e0f2fe;
`;

const ConfirmKey = styled.p`
  font-size: 10px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  margin: 0 0 2px;
`;

const ConfirmVal = styled.p`
  font-size: 12px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
`;

const HighlightItem = styled(ConfirmItem)`
  background: #e0f2fe;
  border-color: #bae6fd;
`;

const FeedbackMsg = styled.p`
  font-size: 12px;
  font-weight: 600;
  color: ${({ $error }) => ($error ? "#ef4444" : "#16a34a")};
  margin: 8px 0 0;
  text-align: right;
`;

// ─── Footer ───────────────────────────────────────────────────────────────────
const ModalFooter = styled.div`
  padding: 14px 24px;
  border-top: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-shrink: 0;
  background: #f8fafc;
`;

const CancelBtn = styled.button`
  padding: 8px 18px;
  background: transparent;
  border: 1.5px solid #e2e8f0;
  border-radius: 9px;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
  &:hover { border-color: #94a3b8; color: #1e293b; }
`;

const SaveBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 20px;
  background: #3f88a5;
  color: #fff;
  border: none;
  border-radius: 9px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, transform 0.1s;
  &:hover:not(:disabled) { background: #2e6d88; transform: translateY(-1px); }
  &:disabled { opacity: 0.55; cursor: not-allowed; transform: none; }
`;

// ─── Constants ────────────────────────────────────────────────────────────────
// ─── Component ────────────────────────────────────────────────────────────────
const ExcelUploadPanel = ({ open, onClose, hook, onSaveSuccess }) => {
  const {
    sources,
    questions,
    financialYears,
    selectedFinancialYear,
    handleFinancialYearChange,
    computePeriodSlots,
    bulkCreateTriggers,
    units,
  } = hook;

  // ── Local form state ──────────────────────────────────────────────────────
  const [periodType,       setPeriodType]       = useState("");
  const [selectedPeriods,  setSelectedPeriods]  = useState([]);
  const [sourceIds,        setSourceIds]        = useState([]);
  const [questionIds,      setQuestionIds]      = useState([]);
  const [filterSteps,      setFilterSteps]      = useState([]);

  // ── Upload state ──────────────────────────────────────────────────────────
  const fileInputRef = useRef(null);
  const [parsedData,  setParsedData]  = useState(null);
  const [parseErrors, setParseErrors] = useState([]);
  const [fileName,    setFileName]    = useState("");
  const [saving,      setSaving]      = useState(false);
  const [saveProgress, setSaveProgress] = useState(0);
  const [successMsg,  setSuccessMsg]  = useState("");
  const [saveError,   setSaveError]   = useState("");
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");
  const didAutoSelectKpisRef = useRef(false);

  // Auto-select all KPIs after a location is picked; clear them when none are picked.
  useEffect(() => {
    if (sourceIds.length === 0) {
      didAutoSelectKpisRef.current = false;
      setQuestionIds([]);
      return;
    }
    if (didAutoSelectKpisRef.current || questions.length === 0) return;
    setQuestionIds(questions.map(q => q.questionId));
    didAutoSelectKpisRef.current = true;
  }, [sourceIds, questions]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const periodSlots = useMemo(
    () => computePeriodSlots(periodType),
    [computePeriodSlots, periodType]
  );

  const fyLabel = financialYears.find(
    fy => String(fy.value) === String(selectedFinancialYear)
  )?.label ?? "";

  const kpiListForFilter = useMemo(
    () => questions.map(q => ({ ...q, id: q.questionId, rawTags: q.tags })),
    [questions]
  );

  const filteredQuestions = useMemo(() => {
    const active = filterSteps.filter(s => s.values?.size);
    if (!active.length) return questions;
    return kpiListForFilter.filter(q => kpiMatchesCascadeSteps(q, filterSteps));
  }, [questions, kpiListForFilter, filterSteps]);

  const canDownload      = questionIds.length > 0 && selectedPeriods.length > 0 && sourceIds.length > 0;
  const downloadRowCount = questionIds.length * sourceIds.length * selectedPeriods.length;

  const parsedRows = useMemo(() => parsedData?.rows ?? [], [parsedData]);
  const totalTriggers = parsedRows.length;

  const confirmMeta = useMemo(() => {
    if (parsedRows.length === 0) return null;
    const fyIds = new Set(parsedRows.map((r) => r.financialYearId));
    const fyId = fyIds.values().next().value;
    const fyLabel = financialYears.find((fy) => String(fy.value) === String(fyId))?.label || `FY #${fyId}`;
    const periodKeys = new Set(parsedRows.map((r) => `${r.fromDate}|${r.toDate}`));
    const sourceIdSet = new Set(parsedRows.map((r) => r.sourceId));
    const kpiSet = new Set(parsedRows.map((r) => r.questionId));
    return {
      financialYearLabel: fyLabel,
      financialYearId: fyId,
      periodCount: periodKeys.size,
      sourceCount: sourceIdSet.size,
      kpiCount: kpiSet.size,
    };
  }, [parsedRows, financialYears]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handlePeriodTypeChange = e => {
    setPeriodType(e.target.value);
    setSelectedPeriods([]);
  };

  const togglePeriod = fromDate => {
    setSelectedPeriods(prev => {
      if (prev.some(p => p.fromDate === fromDate)) return prev.filter(p => p.fromDate !== fromDate);
      const slot = periodSlots.find(s => s.fromDate === fromDate);
      return slot ? [...prev, slot] : prev;
    });
  };

  const handleDownload = async () => {
    setDownloading(true);
    setDownloadError("");
    try {
      await generateTemplate({
        cfg_selectedPeriods: selectedPeriods,
        cfg_sourceIds:       sourceIds,
        cfg_questionIds:     questionIds,
        selectedFinancialYear,
        financialYearLabel:  fyLabel,
        sources,
      });
    } catch (err) {
      setDownloadError(err?.message || "Failed to generate template.");
    } finally {
      setDownloading(false);
    }
  };

  const handleFileChange = async e => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    setParsedData(null);
    setParseErrors([]);
    setSuccessMsg("");
    setSaveError("");
    const { rows, errors: parseErrs } = await parseUploadedTemplate(file, {
      sources,
      questions,
      financialYears,
      units,
    });
    if (parseErrs.length > 0) { setParseErrors(parseErrs); return; }
    const validErrs = validateParsedData(rows);
    if (validErrs.length > 0) { setParseErrors(validErrs); return; }
    setParsedData({ rows });
  };

  const handleClearFile = () => {
    setParsedData(null);
    setParseErrors([]);
    setFileName("");
    setSuccessMsg("");
    setSaveError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSave = async () => {
    if (!parsedData) return;
    setSaving(true);
    setSaveProgress(0);
    setSaveError("");
    setSuccessMsg("");
    const payloads = buildPayloads(parsedData.rows);
    const ok = await bulkCreateTriggers(payloads, setSaveProgress);
    setSaving(false);
    if (ok) {
      const count = payloads.length;
      setSuccessMsg(`✓ ${count} trigger${count !== 1 ? "s" : ""} saved successfully.`);
      handleClearFile();
      if (onSaveSuccess) await onSaveSuccess();
    } else {
      setSaveError("Failed to save triggers. Please try again.");
    }
  };

  const handleClose = () => onClose();

  // ── Render ────────────────────────────────────────────────────────────────
  if (!open) return null;

  return (
    <Overlay onClick={e => { if (e.target === e.currentTarget) handleClose(); }}>
      <ModalBox>

        {/* ── Header ── */}
        <ModalHeader>
          <HeaderIcon>📊</HeaderIcon>
          <HeaderTitle>Excel Bulk Upload</HeaderTitle>
          <HeaderSub>Download → Fill offline → Upload anytime</HeaderSub>
          <CloseBtn type="button" onClick={handleClose}>✕</CloseBtn>
        </ModalHeader>

        <ModalBody>
          <InfoBanner>
            ℹ️ Upload any previously downloaded template directly — all context (financial year, period type, periods, locations, KPIs) is embedded in the file, no re-selection needed. Need a fresh template? Generate one below.
          </InfoBanner>

          {/* ── Upload — always available; context is embedded in the file ── */}
          <Block>
            <BlockHead>
              <BlockTitle>📂 Upload Completed Template</BlockTitle>
            </BlockHead>
            <BlockDesc>
              Drop in any previously downloaded template — values will be saved against the embedded financial year, periods, locations, and KPIs.
            </BlockDesc>

            <input
              id="modal-excel-upload-input"
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />

            <UploadZone
              htmlFor="modal-excel-upload-input"
              $hasFile={!!fileName}
              $hasError={parseErrors.length > 0}
            >
              <UploadIcon>{fileName ? "📄" : "📂"}</UploadIcon>
              <UploadText>
                <UploadPrimary $hasFile={!!fileName}>
                  {fileName || "Choose .xlsx file…"}
                </UploadPrimary>
                {!fileName && <UploadSecondary>Only .xlsx and .xls accepted</UploadSecondary>}
              </UploadText>
            </UploadZone>

            {/* Parse errors */}
            {fileName && parseErrors.length > 0 && (
              <ParseSummary>
                <SummaryLine>✗ File could not be accepted — see errors below.</SummaryLine>
                <ErrorList>
                  {parseErrors.map((err, i) => <ErrorItem key={i}>{err}</ErrorItem>)}
                </ErrorList>
              </ParseSummary>
            )}

            {/* Parse success */}
            {fileName && parsedData && parseErrors.length === 0 && confirmMeta && (
              <ParseSummary $ok>
                <SummaryLine $ok>
                  ✓ {confirmMeta.kpiCount} KPI{confirmMeta.kpiCount !== 1 ? "s" : ""} loaded · {totalTriggers} trigger{totalTriggers !== 1 ? "s" : ""} will be saved
                </SummaryLine>
              </ParseSummary>
            )}

            {/* Confirm card */}
            {parsedData && confirmMeta && (
              <ConfirmCard>
                <ConfirmTitle>Confirm — triggers to be saved</ConfirmTitle>
                <ConfirmGrid>
                  <ConfirmItem>
                    <ConfirmKey>Financial Year</ConfirmKey>
                    <ConfirmVal>{confirmMeta.financialYearLabel}</ConfirmVal>
                  </ConfirmItem>
                  <ConfirmItem>
                    <ConfirmKey>Periods</ConfirmKey>
                    <ConfirmVal>{confirmMeta.periodCount} period{confirmMeta.periodCount !== 1 ? "s" : ""}</ConfirmVal>
                  </ConfirmItem>
                  <ConfirmItem>
                    <ConfirmKey>Locations</ConfirmKey>
                    <ConfirmVal>{confirmMeta.sourceCount} location{confirmMeta.sourceCount !== 1 ? "s" : ""}</ConfirmVal>
                  </ConfirmItem>
                  <HighlightItem>
                    <ConfirmKey>KPIs</ConfirmKey>
                    <ConfirmVal>{confirmMeta.kpiCount}</ConfirmVal>
                  </HighlightItem>
                  <HighlightItem>
                    <ConfirmKey>Total Triggers</ConfirmKey>
                    <ConfirmVal style={{ color: "#0369a1" }}>{totalTriggers}</ConfirmVal>
                  </HighlightItem>
                </ConfirmGrid>
              </ConfirmCard>
            )}

            {successMsg && <FeedbackMsg>{successMsg}</FeedbackMsg>}
            {saveError  && <FeedbackMsg $error>{saveError}</FeedbackMsg>}

            {(parsedData || fileName) && !successMsg && (
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
                <CancelBtn type="button" onClick={handleClearFile}>Clear</CancelBtn>
                {parsedData && parseErrors.length === 0 && (
                  <SaveBtn type="button" onClick={handleSave} disabled={saving}>
                    {saving ? `⏳ Saving… ${saveProgress}%` : "☁ Upload & Save"}
                  </SaveBtn>
                )}
              </div>
            )}
          </Block>

          <Divider />

          <SectionLabel>Don't have a template? Set up below to generate one</SectionLabel>

          {/* Step 1 — Financial Year */}
          <SectionLabel style={{ marginTop: 12 }}>Step 1 — Financial Year</SectionLabel>
          <div style={{ marginBottom: 16 }}>
            <FieldLabel>Financial Year</FieldLabel>
            <StyledSelect
              value={selectedFinancialYear}
              onChange={(e) => handleFinancialYearChange(e.target.value)}
            >
              <option value="">Select year…</option>
              {financialYears.map((fy) => (
                <option key={fy.value} value={fy.value}>{fy.label}</option>
              ))}
            </StyledSelect>
          </div>

          {/* Step 2 — Period Type */}
          {selectedFinancialYear && (
            <>
              <SectionLabel>Step 2 — Period Type</SectionLabel>
              <div style={{ marginBottom: 16 }}>
                <FieldLabel>Period Type</FieldLabel>
                <StyledSelect value={periodType} onChange={handlePeriodTypeChange}>
                  <option value="">Select type…</option>
                  {Object.entries(PERIOD_TYPE_LABELS).map(([val, lbl]) => (
                    <option key={val} value={val}>{lbl}</option>
                  ))}
                </StyledSelect>
              </div>
            </>
          )}

          {/* Steps 3 + 4 */}
          {selectedFinancialYear && periodType && (
            <>
              <TwoCol style={{ marginBottom: 16 }}>
                <div>
                  <SectionLabel>Step 3 — Periods</SectionLabel>
                  <FieldLabel>Periods ({periodSlots.length})</FieldLabel>
                  <TagMultiSelect
                    options={periodSlots}
                    selected={selectedPeriods.map(p => p.fromDate)}
                    getKey={s => s.fromDate}
                    getLabel={s => s.label}
                    placeholder="Select periods…"
                    onToggle={togglePeriod}
                    onSelectAll={keys =>
                      setSelectedPeriods(
                        keys.length === 0 ? [] : periodSlots.filter(s => keys.includes(s.fromDate))
                      )
                    }
                  />
                </div>

                <div>
                  <SectionLabel>Step 4 — Locations</SectionLabel>
                  <FieldLabel>Locations</FieldLabel>
                  <TagMultiSelect
                    options={sources}
                    selected={sourceIds}
                    getKey={s => s.id}
                    getLabel={s => s.unitCode}
                    placeholder="Select locations…"
                    onToggle={id =>
                      setSourceIds(prev =>
                        prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
                      )
                    }
                    onSelectAll={ids => setSourceIds(ids)}
                  />
                </div>
              </TwoCol>

              {/* Step 5 — KPIs */}
              {sourceIds.length > 0 && (
                <>
                  <SectionLabel>Step 5 — KPIs</SectionLabel>
                  <div style={{ marginBottom: 4 }}>
                    <div style={{ marginBottom: 10 }}>
                      <FieldLabel>Filter by Tags</FieldLabel>
                      <KpiCascadeFilter
                        kpiList={kpiListForFilter}
                        filterSteps={filterSteps}
                        onFilterStepsChange={setFilterSteps}
                      />
                    </div>
                    <div>
                      <FieldLabel>KPIs{filterSteps.some(s => s.values?.size) ? " (filtered)" : ""}</FieldLabel>
                      <TagMultiSelect
                        options={filteredQuestions}
                        selected={questionIds}
                        getKey={q => q.questionId}
                        getLabel={q => q.title}
                        placeholder="Select KPIs…"
                        onToggle={id =>
                          setQuestionIds(prev =>
                            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
                          )
                        }
                        onSelectAll={ids => setQuestionIds(ids)}
                      />
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {/* Download — standalone, sits at the bottom of the generate path */}
          <Block style={{ marginTop: 20 }}>
            <BlockHead>
              <BlockTitle>⬇ Download Template</BlockTitle>
            </BlockHead>
            <BlockDesc>
              Get a pre-filled spreadsheet based on your selections above.
              Fill in <strong>Min</strong> and <strong>Max Trigger</strong> values offline, then upload it above.
            </BlockDesc>

            <DownloadBtn type="button" onClick={handleDownload} disabled={!canDownload || downloading}>
              {downloading ? "⏳ Generating…" : "⬇ Download Template"}
            </DownloadBtn>

            {!canDownload ? (
              <DisabledNote>Complete Steps 1–5 above to enable download.</DisabledNote>
            ) : (
              <ChipRow>
                <Chip>📅 {selectedPeriods.length} period{selectedPeriods.length !== 1 ? "s" : ""}</Chip>
                <Chip>📍 {sourceIds.length} location{sourceIds.length !== 1 ? "s" : ""}</Chip>
                <Chip>📈 {questionIds.length} KPI{questionIds.length !== 1 ? "s" : ""}</Chip>
                <Chip>= {downloadRowCount} trigger{downloadRowCount !== 1 ? "s" : ""}</Chip>
              </ChipRow>
            )}

            {downloadError && <FeedbackMsg $error>{downloadError}</FeedbackMsg>}
          </Block>
        </ModalBody>

        <ModalFooter>
          <CancelBtn onClick={handleClose}>Close</CancelBtn>
        </ModalFooter>

      </ModalBox>
    </Overlay>
  );
};

export default ExcelUploadPanel;
